import { type Signal, type TraceDetail } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertOctagon, ChevronDown, ExternalLink, ListTree, MessagesSquare, Radar } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { CopyButton, Hash } from "@/components/domain/code";
import { AgentLink, ImpactList, PageHeader, TimeAgo } from "@/components/domain/misc";
import { OutcomeBadge, SEVERITY_COLOR } from "@/components/domain/status";
import { Page, QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { EmptyState, Field, Skeleton } from "@/components/ui/misc";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Conversation } from "@/features/traces/conversation";
import { SignalCard, SpanInspector } from "@/features/traces/span-inspector";
import { sortSignals } from "@/features/traces/trace-columns";
import { Waterfall } from "@/features/traces/waterfall";
import { q } from "@/lib/api";
import { fmtCost, fmtDateTime, fmtDuration, fmtInt } from "@/lib/format";

const TABS = ["waterfall", "conversation", "signals"] as const;
type Tab = (typeof TABS)[number];

const searchSchema = z.object({
  span: z.string().optional().catch(undefined),
  tab: z.enum(TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/traces/$traceId")({
  validateSearch: searchSchema,
  component: TracePage,
});

function TracePage() {
  const { traceId } = Route.useParams();
  const query = useQuery(q.trace(traceId));
  return (
    <Page wide>
      <QueryState query={query} loading={<TraceSkeleton traceId={traceId} />}>
        {(t) => <TraceView trace={t} />}
      </QueryState>
    </Page>
  );
}

function Breadcrumb({ traceId }: { traceId: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
      <Link to="/traces" className="hover:text-fg hover:underline">
        Traces
      </Link>
      <span aria-hidden>/</span>
      <span className="font-mono" aria-current="page">
        {traceId.slice(0, 12)}…
      </span>
    </nav>
  );
}

function TraceSkeleton({ traceId }: { traceId: string }) {
  return (
    <>
      <PageHeader breadcrumb={<Breadcrumb traceId={traceId} />} title="Trace" />
      <Skeleton className="h-24 w-full" />
      <div className="mt-3 grid grid-cols-[minmax(0,1fr)_400px] gap-3">
        <Skeleton className="h-96" />
        <Skeleton className="h-96" />
      </div>
    </>
  );
}

function TraceView({ trace }: { trace: TraceDetail }) {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab: Tab = search.tab ?? "waterfall";

  const { signalsById, signalsBySpan, issueIds, sortedSignals } = useMemo(() => {
    const byId = new Map(trace.signalDetails.map((s) => [s.id, s]));
    const bySpan = new Map<string, Signal[]>();
    for (const s of trace.signalDetails) {
      if (!s.spanId) continue;
      const list = bySpan.get(s.spanId) ?? [];
      list.push(s);
      bySpan.set(s.spanId, list);
    }
    // Spans may also reference signals via signalIds.
    for (const sp of trace.spans) {
      for (const id of sp.signalIds) {
        const s = byId.get(id);
        if (!s) continue;
        const list = bySpan.get(sp.spanId) ?? [];
        if (!list.includes(s)) list.push(s);
        bySpan.set(sp.spanId, list);
      }
    }
    const sorted = sortSignals(trace.signalDetails);
    const issues = [...new Set(sorted.map((s) => s.issueId).filter((x): x is string => !!x))];
    return { signalsById: byId, signalsBySpan: bySpan, issueIds: issues, sortedSignals: sorted };
  }, [trace]);

  // Default selection: the span carrying the most severe signal, else the root.
  const defaultSpanId = sortedSignals.find((s) => s.spanId && trace.spans.some((sp) => sp.spanId === s.spanId))?.spanId ?? trace.spans.find((s) => !s.parentSpanId)?.spanId;
  const selectedId = search.span && trace.spans.some((s) => s.spanId === search.span) ? search.span : defaultSpanId;
  const selected = trace.spans.find((s) => s.spanId === selectedId);
  const parent = selected?.parentSpanId ? trace.spans.find((s) => s.spanId === selected.parentSpanId) : undefined;

  const select = (spanId: string, nextTab?: Tab) =>
    void navigate({ search: (prev) => ({ ...prev, span: spanId, ...(nextTab ? { tab: nextTab === "waterfall" ? undefined : nextTab } : {}) }), replace: true, resetScroll: false });
  const setTab = (t: string) => void navigate({ search: (prev) => ({ ...prev, tab: t === "waterfall" ? undefined : (t as Tab) }), replace: true, resetScroll: false });

  const controlled = trace.spans.filter((s) => s.control).length;
  const errors = trace.spans.filter((s) => s.status === "error").length;

  return (
    <>
      <PageHeader
        breadcrumb={<Breadcrumb traceId={trace.traceId} />}
        title={
          <span className="flex items-center gap-2">
            Trace
            <Hash value={trace.traceId} n={16} className="text-[13px] font-normal" />
          </span>
        }
        description={<span className="text-fg-2">“{trace.intent}”</span>}
        actions={<TraceActions trace={trace} issueIds={issueIds} />}
      />

      <Card className="mb-3 grid grid-cols-3 gap-x-6 gap-y-3 px-4 py-3 md:grid-cols-6">
        <Field label="Agent">
          <AgentLink id={trace.agentId} name={trace.agentName} />
        </Field>
        <Field label="Version">
          <span className="font-mono text-[12.5px]">{trace.agentVersion}</span>
        </Field>
        <Field label="End user" hint={trace.endUserId ? <span className="font-mono">{trace.endUserId}</span> : undefined}>
          {trace.endUserId ? (
            <Link to="/activity" search={{ affectedId: trace.endUserId }} className="text-fg hover:text-accent hover:underline" title="Everything agents did and said affecting this end user">
              {trace.endUser ?? trace.endUserId}
            </Link>
          ) : (
            <span className="text-fg-3">Internal</span>
          )}
        </Field>
        <Field label="Session">
          <span className="inline-flex items-center gap-0.5 font-mono text-[12px] text-fg-2">
            {trace.sessionId}
            <CopyButton value={trace.sessionId} label="Copy session ID" />
          </span>
        </Field>
        <Field label="Outcome">
          <OutcomeBadge outcome={trace.outcome} />
        </Field>
        <Field label="Started" hint={fmtDateTime(trace.startedAt)}>
          <TimeAgo iso={trace.startedAt} className="text-fg" />
        </Field>
        <Field label="Duration">
          <span className="tabular">{fmtDuration(trace.durationMs)}</span>
        </Field>
        <Field label="Spans" hint={errors ? `${errors} errored` : undefined}>
          <span className="tabular">
            {trace.spanCount}
            {controlled ? <span className="text-fg-3"> · {controlled} controlled</span> : null}
          </span>
        </Field>
        <Field label="Tokens">
          <span className="tabular">{fmtInt(trace.tokens)}</span>
        </Field>
        <Field label="Cost">
          <span className="tabular">{fmtCost(trace.costUsd)}</span>
        </Field>
        <Field label="Impact">
          <ImpactList impact={trace.impact} />
        </Field>
        <Field label="Signals">
          {sortedSignals.length ? (
            <button
              type="button"
              onClick={() => setTab("signals")}
              className="inline-flex cursor-pointer items-center gap-1.5 hover:underline"
            >
              <span className="flex -space-x-0.5" aria-hidden>
                {sortedSignals.slice(0, 4).map((s) => (
                  <span key={s.id} className="size-2 rounded-full ring-2 ring-surface" style={{ background: SEVERITY_COLOR[s.severity] }} />
                ))}
              </span>
              <span className="tabular">
                {sortedSignals.length} <span className="text-fg-3">· worst {sortedSignals[0]!.severity}</span>
              </span>
            </button>
          ) : (
            <span className="text-fg-3">None</span>
          )}
        </Field>
      </Card>

      <div className="grid grid-cols-[minmax(0,1fr)_380px] items-start gap-3 2xl:grid-cols-[minmax(0,1fr)_440px]">
        <Card className="min-w-0 overflow-hidden">
          <Tabs value={tab} onValueChange={setTab}>
            <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
              <TabsList>
                <TabsTrigger value="waterfall">
                  <ListTree className="size-3.5" /> Waterfall
                </TabsTrigger>
                <TabsTrigger value="conversation">
                  <MessagesSquare className="size-3.5" /> Conversation
                  <span className="text-fg-3 tabular">{trace.messages.length}</span>
                </TabsTrigger>
                <TabsTrigger value="signals">
                  <Radar className="size-3.5" /> Signals
                  <span className="text-fg-3 tabular">{trace.signalDetails.length}</span>
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="waterfall" className="focus-visible:outline-none">
              <Waterfall spans={trace.spans} signalsById={signalsById} selectedId={selectedId} onSelect={(id) => select(id)} />
            </TabsContent>
            <TabsContent value="conversation" className="focus-visible:outline-none">
              <Conversation messages={trace.messages} spans={trace.spans} signalsBySpan={signalsBySpan} selectedId={selectedId} onSelect={(id) => select(id)} />
            </TabsContent>
            <TabsContent value="signals" className="focus-visible:outline-none">
              {sortedSignals.length ? (
                <ul className="flex flex-col gap-2 p-4">
                  {sortedSignals.map((s) => {
                    const span = s.spanId ? trace.spans.find((sp) => sp.spanId === s.spanId) : undefined;
                    return (
                      <li key={s.id}>
                        <SignalCard
                          signal={s}
                          className={s.spanId && s.spanId === selectedId ? "border-accent/50" : undefined}
                          action={
                            span ? (
                              <button
                                type="button"
                                onClick={() => select(span.spanId, "waterfall")}
                                className="inline-flex max-w-56 cursor-pointer items-center gap-1 font-mono text-[11.5px] text-fg-2 hover:text-accent hover:underline"
                                title="Show span in waterfall"
                              >
                                <ListTree className="size-3 shrink-0" />
                                <span className="truncate">{span.name}</span>
                              </button>
                            ) : (
                              <span>Trace-level</span>
                            )
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <EmptyState icon={<Radar />} title="No signals on this trace" description="No detector fired on any span. Sampled LLM judges and fleet statistics may still add signals later." />
              )}
            </TabsContent>
          </Tabs>
        </Card>

        <aside aria-label="Span inspector" className="sticky top-4 min-w-0">
          <Card className="scrollbar-thin max-h-[calc(100vh-88px)] overflow-y-auto">
            <SpanInspector span={selected} parentName={parent?.name} signals={selected ? (signalsBySpan.get(selected.spanId) ?? []) : []} />
          </Card>
        </aside>
      </div>
    </>
  );
}

function TraceActions({ trace, issueIds }: { trace: TraceDetail; issueIds: string[] }) {
  return (
    <>
      {issueIds.length === 1 ? (
        <Button variant="outline" asChild>
          <Link to="/issues/$issueId" params={{ issueId: issueIds[0]! }}>
            <AlertOctagon /> Open issue {issueIds[0]}
          </Link>
        </Button>
      ) : issueIds.length > 1 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <AlertOctagon /> Open issue <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {issueIds.map((id) => (
              <DropdownMenuItem key={id} asChild>
                <Link to="/issues/$issueId" params={{ issueId: id }}>
                  <ExternalLink /> {id}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {trace.sessionId !== trace.traceId ? (
        <Button variant="outline" asChild>
          <Link to="/sessions/$sessionId" params={{ sessionId: trace.sessionId }}>
            <MessagesSquare /> Open session
          </Link>
        </Button>
      ) : null}
    </>
  );
}
